import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import getRandomProfile from "@/assets/profile";
import { AnimatePresence, motion } from "framer-motion";
import {
  Button,
  Checkbox,
  Form,
  Input,
  notification,
  Select,
  Steps,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { Swiper, SwiperSlide } from "swiper/react";
import Link from "next/link";
import cx from "classnames";
import { isEmpty, lowerCase } from "lodash";

import Avatar from "../Avatar";
import CropAvatar from "../Avatar/CropAvatar";
import { DiamondWhite, Star } from "@/assets/icons";
import { IAuthProps } from "@/hooks/useAuth";
import useAuthStore from "@/store/useAuthStore";
import useWindowResize from "@/hooks/useWindowResize";
import useWordsConversionEnv from "@/hooks/useWordsConversionEnv";
import useEnvironmentStore from "@/store/useEnvironmentStore";
import {
  updateProfile,
  useGetUserProfile,
  useUserFetchInitially,
} from "@/hooks/useUsers";
import { useIsViewAsHR } from "@/hooks/useIsViewAsHR";

import { useGetOrgSettings } from "@/hooks/useOrg";
import { useGetDepartments } from "@/hooks/useGoals";
import { getListInterests, IInterests } from "@/hooks/useInterest";
import { useSetUserDashboardConfig } from "@/hooks/useDashboard";
import { getSession } from "next-auth/react";
import { uploadFile } from "@/hooks/useUploadFile";
import { PAGE_URL } from "@/utils/constants";
import { imageToBase64 } from "@/utils/helper";
import { RcFile } from "antd/es/upload";
import { UploadChangeParam, UploadFile } from "antd/lib/upload";

interface OnboardingTypes extends IAuthProps { }

export default function OnBoarding() {
  const { user } = useAuthStore();

  const { convertWords } = useWordsConversionEnv();

  const { fetchUser } = useUserFetchInitially();

  const orgString = user?.bio?.user?.organizationGroup;
  const { data: dataOrgSettings = [] }: { data: any } = useGetOrgSettings(
    orgString,
    {
      enabled: !!orgString,
    }
  );
  const isMobile = useWindowResize();
  const { env, module } = useEnvironmentStore();
  const [currentStep, currentStepSet] = useState(0);
  const [agreementValidated, agreementValidatedSet] = useState({
    about: false,
    privacyPolicy: false,
  });

  const [linkedInPict, linkedInPictSet] = useState("");
  const [isImportWithLinkedIn, isImportWithLinkedInSet] = useState(false);

  const [selectedInterest, selectedInterestSet] = useState<{
    [keys: string]: string[];
  }>({});

  const LINKEDIN_PREFIX = "https://www.linkedin.com/in/";

  const cleaned = (s: string) => s.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 100);
  const toSlug = (v: string) => {
    const t = (v || "").trim();
    if (!/^https?:\/\//i.test(t)) return cleaned(t);
    try {
      const u = new URL(t);
      const i = u.pathname.toLowerCase().indexOf("/in/");
      const afterIn = i >= 0 ? u.pathname.slice(i + 4) : u.pathname;
      const seg = afterIn.split("/").filter(Boolean)[0] ?? "";
      return cleaned(seg);
    } catch {
      return cleaned(t);
    }
  };

  const [welcomeContent, welcomeContentSet] = useState([
    {
      key: "Welcome to MyConnect.ai",
      value:
        "Whenever you see the diamond you have the opportunity to earn points. Points are redeemable for rewards.",
    },
    {
      key: "Connect",
      value:
        "Join Circles, RSVP for Events and Connect with people to further your career.",
    },
    {
      key: "Get Started",
      value: "Make your first connection to earn 200 points today.",
    },
    {
      key: "Follow The Stars!",
      value: (
        <div className="mb-6 flex flex-col gap-6">
          <p>
            Let our stars guide you to find the best connections, circles, and
            events for you.
          </p>
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Star color="green" />
              <p>Strongly Recommended</p>
            </div>
            <div className="flex items-center gap-2">
              <Star color="yellow" />
              <p>Definitely Something to Consider</p>
            </div>
            <div className="flex items-center gap-2">
              <Star color="salmon" />
              <p>
                {env === "ml" || env === "hbas" || module === "conference"
                  ? "Insufficient Data Points"
                  : "It's Not an Ideal Match"}
              </p>
            </div>
          </div>
        </div>
      ),
    },
  ]);
  const [welcomeStep, welcomeStepset] = useState(0);

  const [onboardingMode, onboardingModeSet] = useState<"onboard" | "welcome">(
    "onboard"
  );

  const [isLoadingLast, isLoadingLastSet] = useState(false);

  const [jobTitleInput, jobTitleInputSet] = useState("");
  const [organizationInput, setOrganizationInput] = useState("");
  const completeJobTitle = `${jobTitleInput}${organizationInput && ` at ${organizationInput}`}`;

  const [departmentInput, departmentInputSet] = useState();
  const [avatarFile, avatarFileSet] = useState<null | any>();
  const [avatarPreview, avatarPreviewSet] = useState<null | any>();
  const { organizationGroup } = useIsViewAsHR();
  const [linkedInErrorMessage, linkedInErrorMessageSet] = useState("");
  // const { data: dataInterestListRaw } = useListInterests(organizationGroup);
  const [dataInterestListRaw, setDataInterestListRaw] = useState<
    IInterests[] | null
  >(null);

  const dataInterestList = useMemo(() => {
    if (!dataInterestListRaw) return [];
    const filteredNonDeleted = dataInterestListRaw
      ?.filter((x) => !x?._deleted)
      ?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
    // Sort by name (case-insensitive), prioritize "Vacation", "Music", "Food" at the top
    const priority = ["vacation", "music", "food"];
    const sorted = filteredNonDeleted.sort((a, b) => {
      const aName = (a?.name || "").toLowerCase();
      const bName = (b?.name || "").toLowerCase();

      const aPriority = priority.indexOf(aName);
      const bPriority = priority.indexOf(bName);

      if (aPriority !== -1 && bPriority !== -1) {
        return aPriority - bPriority;
      }
      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;

      return aName.localeCompare(bName);
    });
    return sorted;
  }, [dataInterestListRaw]);

  const {
    data: dataDepartments,
    refetch: fetchDepartments,
    isFetching: isLoadingGetDepartments,
  } = useGetDepartments(true);

  const fetchInterests = useCallback(async () => {
    if (!organizationGroup) return;
    const dataInterests = await getListInterests(organizationGroup);
    setDataInterestListRaw(dataInterests);
  }, [organizationGroup]);

  useEffect(() => {
    fetchInterests();
  }, [organizationGroup]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    timeout = setTimeout(() => {
      fetchDepartments();
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!dataInterestListRaw) return;
    dataInterestList?.forEach((interest) => {
      if (!selectedInterest[interest?.name?.split(" ")?.join("_") || "_"]) {
        selectedInterestSet((prev) => ({
          ...prev,
          [interest?.name?.split(" ")?.join("_") || "_"]: [],
        }));
      }
    });
  }, [dataInterestList]);

  const [notificationAPI, contextHolder] = notification.useNotification();

  const [openModalCropper, openModalCropperSet] = useState(false);
  const [avatarAfterCropFile, avatarAfterCropFileSet] = useState<File | null>(
    null
  );
  const [avatarAfterCrop, setAvatarAfterCrop] = useState<string | null>(null);
  const previewImageRef = useRef<string | null>(null);

  const [isLoadingSubmitProfile, isLoadingSubmitProfileSet] =
    useState<boolean>(false);
  const [isLoadingSubmitInterest, isLoadingSubmitInterestSet] =
    useState<boolean>(false);
  const { mutateAsync } = useSetUserDashboardConfig();
  const { refetch: refetchUserProfile } = useGetUserProfile(user?.profile?.id);

  function setStep(dir: "next" | "prev") {
    if (dir === "next" && currentStep + 1 < steps.length) {
      currentStepSet((prev) => prev + 1);
      window.localStorage.setItem("onboard-step", (currentStep + 1).toString());
    }
    if (dir === "prev" && currentStep > 0) {
      currentStepSet((prev) => prev - 1);
      window.localStorage.setItem("onboard-step", (currentStep - 1).toString());
    }
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      const getStep = window.localStorage.getItem("onboard-step");
      const getIfUseLinkedInImport = window.localStorage.getItem(
        "use-import-linkedin"
      );
      if (getStep) {
        currentStepSet(parseInt(getStep));
      }

      if (getIfUseLinkedInImport) {
        isImportWithLinkedInSet(getIfUseLinkedInImport === "true");
      }
    }
  }, []);

  const anim = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
    },
    exit: {
      opacity: 0,
    },
  };

  const [linkedInForm] = Form.useForm<{ linkedInURLInput: string }>();
  const linkedInURLInput = Form.useWatch(["linkedInURLInput"], linkedInForm);

  async function submitProfile() {
    isLoadingSubmitProfileSet(true);
    let linkedIn = linkedInURLInput ? LINKEDIN_PREFIX + linkedInURLInput : "";
    if (!linkedIn) {
      linkedInErrorMessageSet("Please enter your current LinkedIn username\n(e.g., linkedin.com/in/john-doe)");
      isLoadingSubmitProfileSet(false);
      return;
    } else {
      linkedInErrorMessageSet("");
    }
    try {
      if (avatarPreview) {
        const keyImg = `users/avatar/${Date.now()}-${avatarFile?.name}`;

        await uploadFile({
          key: keyImg,
          file: avatarAfterCropFile || avatarPreview,
        });
        await updateProfile({
          id: user?.bio?.userId,
          jobTitle: completeJobTitle,
          avatarUrl: avatarAfterCropFile ? keyImg : avatarPreview,
          userDepartmentId: departmentInput,
          _version: user?.bio?.user?._version,
          socialMedia: {
            linkedIn,
          },
        });
      } else {
        await updateProfile({
          id: user?.bio?.userId,
          jobTitle: completeJobTitle,
          _version: user?.bio?.user?._version,
          userDepartmentId: departmentInput,
          avatarUrl: isImportWithLinkedIn ? linkedInPict : undefined,
          socialMedia: {
            linkedIn,
          },
        });
      }

      isLoadingSubmitProfileSet(false);
      setStep("next");
      await fetchUser();
    } catch (error) {
      isLoadingSubmitProfileSet(false);
    }
  }

  async function submitInterest() {
    try {
      const combinedArray = Object.values(selectedInterest).reduce(
        (result, currentArray) => {
          return result.concat(currentArray);
        },
        []
      );

      await updateProfile({
        interest: combinedArray,
        id: user?.bio?.userId,
        _version: user?.bio?.user?._version,
      });
    } catch (error) {
      isLoadingSubmitInterestSet(false);
    }
  }

  async function destroyByebye() {
    destroyedSet(true);

    window.localStorage.removeItem("onboard-step");
    window.localStorage.removeItem("use-import-linkedin");
    document.body.style.removeProperty("overflow");
  }

  const keyInterest = Object.keys(selectedInterest).map((key) => key);

  const nextBtnAppearInterest = useMemo(() => {
    // Check that every interest group has at least 3 selected tags
    return (
      keyInterest.length > 0 &&
      keyInterest.every((key) => selectedInterest[key]?.length >= 3)
    );
  }, [selectedInterest, keyInterest]);

  async function endOnboarding() {
    isLoadingLastSet(true);
    const combinedArray = Object.values(selectedInterest).reduce(
      (result, currentArray) => {
        return result.concat(currentArray);
      },
      []
    );
    await updateProfile({
      id: user?.bio?.userId,
      interest: combinedArray,
      _version: user?.bio?.user?._version,
      isOnboarded: true,
    });

    isLoadingLastSet(false);

    const dashboardConfigInit = [
      ["MOTIVATIONAL_QUOTES"],
      [],
      ["RECOMMENDED_CONNECTION", "RECOMMENDED_CIRCLE", "RECOMMENDED_EVENT"],
      [],
      ["UPCOMING_REWARDS"],
    ];

    await mutateAsync({
      config: JSON.stringify(dashboardConfigInit),
      disabledQuickQuestion: false,
    });

    await fetchUser();
    await refetchUserProfile();
    await destroyByebye();

  }

  async function onClickNext() {
    isLoadingSubmitInterestSet(true);
    await endOnboarding();
    window.localStorage.removeItem("onboard-step");
    window.localStorage.removeItem("use-import-linkedin");
    document.body.style.removeProperty("overflow");
    isLoadingSubmitInterestSet(false);
  }

  const tagsProcessed = (tags: any[]) => {
    if (!tags) return [];
    const findOther = { ...tags?.find((x) => x?.name === "Other") };
    const filterOther = tags
      ?.filter((x) => x?.name !== "Other" && !x?._deleted)
      ?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
    if (!isEmpty(findOther)) {
      filterOther?.push(findOther);
    }
    return filterOther;
  };

  async function onChangeEditAvatar(e: UploadChangeParam<UploadFile<any>>) {
    previewImageRef.current = await imageToBase64(
      e?.file?.originFileObj as RcFile
    );
    openModalCropperSet(true);
    avatarFileSet(e.fileList[0]);
    window.localStorage.removeItem("use-import-linkedin");
    isImportWithLinkedInSet(false);
  }

  const steps = [
    {
      title: <p>User's Agreement</p>,
      content: (
        <motion.div {...anim} key="user-agreement" className="max-w-lg mx-auto">
          <h3 className="font-medium text-2xl text-center mb-8 lg:mb-3">
            User's Agreement
          </h3>
          <div
            id="agreement-content"
            className={`flex flex-col gap-5 mt-14 mb-5`}
          >
            {[
              {
                key: "about",
                label: "Disclaimers, Waivers, and Terms & Conditions.",
              },
              {
                key: "privacyPolicy",
                label: "Privacy Policy.",
              },
            ].map((item, i) => (
              <div key={i} className="flex flex-col gap-2">
                <Checkbox
                  checked={agreementValidated[item.key as never]}
                  onChange={(e) =>
                    agreementValidatedSet((prev) => ({
                      ...prev,
                      [item.key]: e.target.checked,
                    }))
                  }
                >
                  I have read and agree to the{" "}
                  <Link href={PAGE_URL[item.key as never]} target="_blank">
                    <span className="text-yellow">{item?.label}</span>
                  </Link>
                </Checkbox>
              </div>
            ))}
          </div>
          <div className="mb-8">
            <Checkbox
              checked={!Object.values(agreementValidated).includes(false)}
              onChange={(e) => {
                agreementValidatedSet((prev) => ({
                  about: e.target.checked,
                  privacyPolicy: e.target.checked,
                }));
              }}
            >
              I have read and agree to all of the above.
            </Checkbox>
          </div>
          <div className="gap-4 flex justify-end mt-5">
            <Button
              disabled={Object.values(agreementValidated).includes(false)}
              type="primary"
              onClick={() => setStep("next")}
            >
              Next
            </Button>
          </div>
        </motion.div>
      ),
    },
    {
      title: <p>Profile</p>,
      content: (
        <motion.div
          {...anim}
          key="profile"
          className="max-w-xl mx-auto pb-[80px] md:pb-0"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 mt-10 sm:gap-5 md:gap-8">
            <div
              className={`relative flex flex-col items-center justify-between gap-2 ${isMobile ? "" : ""}`}
            >
              {isImportWithLinkedIn ? (
                <Avatar alt="avatar" staticImg src={linkedInPict} size="xl" />
              ) : user?.bio?.user?.avatarUrl && !avatarAfterCrop ? (
                <Avatar
                  alt="avatar"
                  src={user?.bio?.user?.avatarUrl}
                  size="xl"
                  editIcon
                  editCrop={avatarAfterCrop ? true : false}
                  onEditCropClicked={() => openModalCropperSet(true)}
                  onChangeEdit={onChangeEditAvatar}
                />
              ) : (
                <div className="w-full flex justify-center flex-col items-center gap-2">
                  <Avatar
                    alt="avatar"
                    staticImg
                    src={avatarAfterCrop || getRandomProfile("User")}
                    size="xl"
                    editIcon
                    editCrop={avatarAfterCrop ? true : false}
                    onEditCropClicked={() => openModalCropperSet(true)}
                    onChangeEdit={onChangeEditAvatar}
                  />
                </div>
              )}
              <div className="text-center">
                <p className="text-[8px]">{completeJobTitle}</p>
                <p className="text-sm font-bold">
                  {user?.bio?.user?.firstName} {user?.bio?.user?.lastName}
                </p>
                <p className="text-xs w-full md:w-auto text-center md:text-start">
                  Personalize your profile with a photo.
                </p>
              </div>

              <Form form={linkedInForm} layout="vertical" className="w-full">
                <Form.Item name="linkedInURLInput" normalize={(v) => toSlug(v)}>
                  <p className="py-1.5">{isMobile ? "LinkedIn URL" : ""}</p>
                  <Input
                    // SAME rounded box; prefix stays inside
                    required={true}
                    prefix={
                      <span className="mr-[-3px]">{LINKEDIN_PREFIX}</span>
                    }
                    placeholder="username"
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    autoComplete="off"
                    inputMode="url"
                    className="bg-transparent placeholder-gray-100 self-end"
                    value={linkedInForm.getFieldValue("linkedInURLInput")}
                    onChange={(e) => {
                      linkedInForm.setFieldValue(
                        "linkedInURLInput",
                        e.target.value
                      );
                    }}
                    style={{
                      borderColor:
                        !linkedInForm.getFieldValue("linkedInURLInput") &&
                          linkedInErrorMessage
                          ? "red"
                          : undefined,
                    }}
                    onPaste={(e) => {
                      const text = e.clipboardData.getData("text");
                      e.preventDefault();
                      linkedInForm.setFieldValue(
                        "linkedInURLInput",
                        toSlug(text)
                      );
                    }}
                  />
                </Form.Item>
              </Form>
              <p className="absolute text-center text-red-500 bottom-[-12px] text-xs whitespace-pre">
                {linkedInForm.getFieldValue("linkedInURLInput")
                  ? ""
                  : linkedInErrorMessage}
              </p>
            </div>
            <div>
              <Form layout="vertical">
                <Form.Item label={convertWords("job_title_label")}>
                  <Input
                    placeholder={convertWords("job_title_placeholder")}
                    value={jobTitleInput}
                    onChange={(e) => jobTitleInputSet(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label={convertWords("organization_label")}>
                  <Input
                    placeholder={convertWords("organization_placeholder")}
                    value={organizationInput}
                    onChange={(e) => setOrganizationInput(e.target.value)}
                  />
                </Form.Item>
                <Form.Item
                  label={convertWords("onboarding_select_label_department")}
                >
                  <Select
                    placeholder={convertWords(
                      "onboarding_select_placeholder_department"
                    )}
                    loading={isLoadingGetDepartments}
                    options={
                      dataDepartments
                        ? dataDepartments.map((item) => ({
                          label: item?.name,
                          value: item?.id,
                        }))
                        : []
                    }
                    value={departmentInput}
                    onChange={(e) => departmentInputSet(e)}
                  />
                </Form.Item>
              </Form>
            </div>
          </div>
          <div className="flex lg:justify-end gap-4">
            <Button onClick={() => setStep("prev")}>Back</Button>
            <Button
              disabled={
                !jobTitleInput ||
                (dataDepartments?.length !== 0 && !departmentInput)
              }
              onClick={() => submitProfile()}
              type="primary"
              loading={isLoadingSubmitProfile}
              className="flex-1 lg:flex-none mb-4"
            >
              Next
            </Button>
          </div>
          <CropAvatar
            openModalCropper={openModalCropper}
            openModalCropperSet={openModalCropperSet}
            avatarAfterCropFileSet={avatarAfterCropFileSet}
            setAvatarAfterCrop={setAvatarAfterCrop}
            previewImageRef={previewImageRef}
            wrapperClassName="!z-[10000]"
            maskStyle={{
              zIndex: 9999,
            }}
          />
        </motion.div>
      ),
    },
    {
      title: <p>Interests</p>,
      content: (
        <motion.div
          {...anim}
          key="interest"
          className="max-w-3xl mx-auto h-full flex flex-col"
        >
          <div
            className={`flex flex-col gap-4 h-[calc(100vh-80vw)] ${isMobile && "overflow-y-auto"} lg:flex-1 lg:h-auto`}
          >
            <AnimatePresence>
              {dataInterestList?.map((item, ii) => (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.8,
                      type: "spring",
                      delay: 0.55,
                    },
                  }}
                  exit={{
                    opacity: 0,
                    y: -10,
                    transition: { duration: 0.5, type: "spring" },
                  }}
                  key={ii}
                >
                  <div className="mb-2">
                    <h3 key={ii} className="lg:text-lg font-bold text-center">
                      {item?.name}
                    </h3>
                    <p className="text-center text-xs lg:text-sm">
                      Choose at least 3 {lowerCase(item?.name || "")} interests.
                    </p>
                  </div>
                  <div
                    key={ii}
                    className={cx(
                      "flex gap-2 flex-wrap justify-center items-center"
                    )}
                  >
                    {item?.tags &&
                      tagsProcessed(item?.tags?.items || [])?.map((tag, i) => (
                        <div
                          key={i}
                          className={`border-primary border py-[.5rem] px-[1rem] lg:py-2 lg:px-4 rounded-full text-white font-bold cursor-pointer 
                  hover:bg-primary hover:bg-opacity-50 transition-all text-xs
                  ${selectedInterest[keyInterest[ii]]?.includes(
                            tag?.id as string
                          ) && "bg-primary hover:bg-opacity-100"
                            }                  
                  `}
                          onClick={() =>
                            selectedInterestSet((prev) => {
                              let sample = { ...prev }[keyInterest[ii]];
                              if (!sample?.includes(tag?.id as string)) {
                                sample.push(tag?.id as string);
                              } else {
                                sample = sample.filter((x) => x !== tag?.id);
                              }
                              return {
                                ...prev,
                                [keyInterest[ii]]: sample,
                              };
                            })
                          }
                        >
                          {tag?.name}
                        </div>
                      ))}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="bg-card justify-between items-center flex fixed lg:relative bottom-0 left-0 right-0 px-4 lg:px-0 flex-auto pb-4 lg:pb-0">
            <motion.div>
              <Button onClick={() => setStep("prev")} type="primary">
                Back
              </Button>
            </motion.div>
            {/* indicator */}
            <div />
            <AnimatePresence>
              <motion.div
                key="next"
                initial={{ opacity: 0 }}
                animate={{ opacity: nextBtnAppearInterest ? 1 : 0 }}
                exit={{ opacity: 0 }}
              >
                <Button
                  loading={isLoadingSubmitInterest}
                  disabled={!nextBtnAppearInterest}
                  onClick={onClickNext}
                  type="primary"
                >
                  Done
                </Button>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      ),
    },
  ];

  const swiperRef = useRef<any>(null);

  const [destroyed, destroyedSet] = useState(true);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    if (user?.bio?.user?.isOnboarded) {
      destroyByebye();
      document.body.style.removeProperty("overflow");
    }
    if (
      user?.bio?.user?.isOnboarded === false ||
      user?.bio?.user?.isOnboarded === null
    ) {
      localStorage.setItem("isOnboarded", "false");
      destroyedSet(false);
    }

    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, [user?.bio?.user?.isOnboarded]);

  useEffect(() => {
    if (swiperRef?.current) {
      swiperRef?.current?.slideTo?.(3);
    }
  }, [currentStep]);

  useEffect(() => {
    async function mySession() {
      const session = await getSession();
      return session;
    }
    mySession().then((res) => {
      if (res) {
        linkedInPictSet(res?.user?.image as string);
      }
    });
  }, []);

  useEffect(() => {
    if (avatarFile) {
      avatarPreviewSet(
        avatarFile?.originFileObj
          ? URL.createObjectURL(avatarFile?.originFileObj)
          : null
      );
      return;
    }

    avatarPreviewSet(null);
  }, [avatarFile]);

  return (
    <AnimatePresence>
      {contextHolder}
      {!destroyed && (
        <motion.div
          key="onboarding-component"
          exit={{ opacity: 0 }}
          className={cx(
            "h-screen w-full bg-black fixed z-[1030] flex flex-col items-center",
            {
              "bg-opacity-90": onboardingMode === "onboard",
              "bg-opacity-30 justify-center": onboardingMode === "welcome",
              "translate-y-36":
                onboardingMode === "welcome" && !isMobile && welcomeStep !== 4,
            }
          )}
          style={{ transition: ".5s" }}
        >
          <AnimatePresence>
            {onboardingMode === "onboard" && (
              <motion.div
                key="onboard"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.5 } }}
                className="mx-auto w-full h-screen bg-card overflow-hidden overflow-y-auto lg:overflow-y-hidden"
              >
                <div
                  className="w-full h-20 bg-no-repeat bg-cover bg-top"
                  style={{
                    background:
                      "linear-gradient(to bottom, #158284 0%, #000 100%)",
                  }}
                />
                <div className="py-6 h-[calc(100vh-5rem)] lg:px-8 px-3">
                  <div className="pb-5 hidden lg:block">
                    <Steps current={currentStep} items={steps} />
                  </div>
                  <div className="pb-5 lg:hidden">
                    <Swiper
                      initialSlide={currentStep}
                      allowTouchMove={false}
                      slidesPerView={1}
                      onBeforeInit={(swiper: any) =>
                        (swiperRef.current = swiper)
                      }
                    >
                      {steps.map((item, i) => (
                        <SwiperSlide key={i}>
                          <div className="flex items-center gap-2">
                            <div className="flex min-w-max items-center gap-2">
                              <div className="bg-primary rounded-full w-8 h-8 flex items-center justify-center">
                                <div className="text-white">{i + 1}</div>
                              </div>
                              <span>{item?.title}</span>
                            </div>
                            <div className="flex-1 h-[2px] bg-white" />
                            {i + 1 < steps.length && (
                              <div className="bg-transparent border-white border rounded-full w-8 h-8 flex items-center justify-center">
                                <div className="text-white">{i + 2}</div>
                              </div>
                            )}
                            {i + 1 === steps.length && (
                              <div className="bg-transparent border-primary border rounded-full w-8 h-8 flex items-center justify-center">
                                <div className="text-primary">
                                  <CheckOutlined />
                                </div>
                              </div>
                            )}
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>

                  <AnimatePresence>
                    <motion.div className="pt-2 lg:pt-5 lg:h-full overflow-y-auto hide-scrollbar mx-auto">
                      {steps[currentStep].content}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {onboardingMode === "welcome" && (
            <OnBoardWelcome
              dataOnBoard={
                dataOrgSettings?.length !== 0
                  ? [...dataOrgSettings[0]?.onboarding]
                  : [...welcomeContent]
              }
              isLoadingLast={isLoadingLast}
              welcomeStep={welcomeStep}
              welcomeStepset={welcomeStepset}
              onFinish={endOnboarding}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const OnBoardWelcome = ({
  dataOnBoard,
  welcomeStep,
  welcomeStepset,
  isLoadingLast,
  onFinish,
}: {
  dataOnBoard: any;
  welcomeStep: number;
  welcomeStepset: any;
  isLoadingLast: boolean;
  onFinish: any;
}) => {
  // const { convertWords } = useWordsConversionEnv();
  const { env, module } = useEnvironmentStore();
  const { convertWords } = useWordsConversionEnv();
  return (
    <AnimatePresence mode="wait">
      {dataOnBoard &&
        dataOnBoard.map(
          (item: any, i: number) =>
            i === welcomeStep && (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { duration: 0.5, delay: 0.55 },
                }}
                exit={{ opacity: 0 }}
                className={`max-w-[350px] lg:max-w-lg mx-auto w-full bg-card overflow-hidden mb-8 py-8 px-8 border border-white`}
              >
                <div className="flex justify-center mb-4">
                  <DiamondWhite size={40} />
                </div>
                <h3 className="font-bold text-xl lg:text-2xl mb-4">
                  {item?.key}
                </h3>
                {typeof item?.value === "string" ? (
                  <>
                    <p className="text-base lg:text-lg font-normal">
                      {item?.value}
                    </p>
                    {item?.key === "Follow the Stars!" && (
                      <div className="flex flex-col gap-4 mt-4">
                        <div className="flex items-center gap-2">
                          <Star color="green" />
                          <p>Strongly Recommended</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Star color="yellow" />
                          <p>Definitely Something to Consider</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Star color="salmon" />
                          <p>
                            {env === "ml" ||
                              env === "hbas" ||
                              module === "conference"
                              ? "Insufficient Data Points"
                              : "It's Not an Ideal Match"}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  item?.value
                )}
                <div className="flex justify-end gap-4">
                  {welcomeStep > 0 && (
                    <Button
                      onClick={() => welcomeStepset((prev: any) => prev - 1)}
                      className="mt-8"
                    >
                      Back
                    </Button>
                  )}

                  {welcomeStep !== dataOnBoard?.length - 1 && (
                    <Button
                      onClick={() => welcomeStepset((prev: any) => prev + 1)}
                      type="primary"
                      className="mt-8"
                    >
                      Next
                    </Button>
                  )}
                  {welcomeStep === dataOnBoard?.length - 1 && (
                    <Button
                      onClick={onFinish}
                      loading={isLoadingLast}
                      type="primary"
                      className="mt-8"
                    >
                      Finish
                    </Button>
                  )}
                </div>
              </motion.div>
            )
        )}
    </AnimatePresence>
  );
};
